import React, {useState, createContext, useContext, useEffect} from 'react';
import {secureRetrieve, secureStore} from "../utils/localStore";
import {ACCESS_TOKEN_TTL, PROFILE_DATA, REFRESH_TOKEN_TTL} from "../constants/globalConstant";
import utils from "../utils/commonFunctions";
import {useNavigate} from "react-router-dom";
import Preloader from "../components/Preloader/Preloader";
// Note: Preloader is only shown for user-triggered loading (logout / triggerAuthProcess).
// Initial auth check is handled visually by AppBootstrap — no second spinner here.

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingLogOut, setLoadingLogOut] = useState(false);
    const [loadingInitialProfileLoad, setLoadingInitialProfileLoad] = useState(false);
    const [loadingTriggerAuthProcess, setLoadingTriggerAuthProcess] = useState(false);
    const [role, setRole] = useState(null);
    const [tokenExpiresIn, setTokenExpiresIn] = useState(() => {
        const raw = secureRetrieve(ACCESS_TOKEN_TTL);
        return raw ? parseInt(raw) : null;
    });
    const [refreshTokenExpiresIn, setRefreshTokenExpiresIn] = useState(() => {
        const raw = secureRetrieve(REFRESH_TOKEN_TTL);
        return raw ? parseInt(raw) : null;
    });


    const triggerAuthProcess = async ({profile = null}) => {
        setLoadingTriggerAuthProcess(true);
        const authenticated = await utils.isAuthenticated()
        await utils.loadNewApiKey()

        if(profile){
            const {role,atl,rtl} = profile

            if(role){
                storeRole(role)
            }
            if(atl){
                storeAccessTokenExpiresTTl(atl)
            }
            if(rtl){
                storeRefreshTokenExpiresTTl(rtl)
            }

            storeProfileData(profile)
        }
        setIsAuthenticated(authenticated)
        setLoadingTriggerAuthProcess(false);
    }

    const storeRefreshTokenExpiresTTl = (value) => {
        setRefreshTokenExpiresIn(parseInt(value))
        secureStore(REFRESH_TOKEN_TTL, value)
    }

    const storeAccessTokenExpiresTTl = (value) => {
        setTokenExpiresIn(parseInt(value))
        secureStore(ACCESS_TOKEN_TTL, value)
    }

    const storeProfileData = (data) => {
        secureStore(PROFILE_DATA, JSON.stringify(data))
    }

    const storeRole = (value) => {
        setRole(value)
    }

    const logout = async () => {
        localStorage.removeItem(PROFILE_DATA)
        localStorage.removeItem(ACCESS_TOKEN_TTL)
        localStorage.removeItem(REFRESH_TOKEN_TTL)
        setIsAuthenticated(false);
        setTokenExpiresIn(null);
        setRefreshTokenExpiresIn(null);
        setRole(null);
        await utils.logout()
    }


    useEffect(() => {
        const init = async () => {
            // Runs silently — AppBootstrap already showed the visual loading step
            let authenticated = await utils.isAuthenticated();
            const profile = utils.getLocalProfile();

            if (profile) {
                const { role, atl, rtl } = profile;

                if (role) storeRole(role);
                if (atl) storeAccessTokenExpiresTTl(atl);
                if (rtl) storeRefreshTokenExpiresTTl(rtl);

                storeProfileData(profile);
            }

            if (!authenticated) {
                // attempt token refresh
                try {
                    const refresh = await utils.refreshToken();
                    if (refresh?.success) {
                        console.log('[init] Refresh successful. Applying new profile...');
                        await triggerAuthProcess({ profile: refresh.data });
                    } else {
                        console.warn('[init] Refresh failed. Logging out...');
                        await logout();
                    }
                } catch (err) {
                    console.error('[init] Token refresh error:', err);
                    await logout();
                }
            } else {
                setIsAuthenticated(true);
            }

            setLoadingInitialProfileLoad(false);
        };

        init();
    }, []);

    useEffect(() => {
        setLoading(loadingLogOut || loadingInitialProfileLoad || loadingTriggerAuthProcess)
    }, [loadingLogOut,loadingInitialProfileLoad,loadingTriggerAuthProcess])


    return (
        <AuthContext.Provider value={{
            redirectUrl, setRedirectUrl,
            isAuthenticated,
            setIsAuthenticated,
            loading,
            role,
            storeRole,
            refreshTokenExpiresIn,
            tokenExpiresIn,
            storeRefreshTokenExpiresTTl,
            storeAccessTokenExpiresTTl,
            storeProfileData,
            triggerAuthProcess,
            logout
        }}>
            {loading ? <Preloader /> : children}
            {/*<pre>*/}
            {/*{JSON.stringify(*/}
            {/*    {*/}
            {/*        redirectUrl,*/}
            {/*        isAuthenticated,*/}
            {/*        loading,*/}
            {/*        loadingLogOut,*/}
            {/*        loadingInitialProfileLoad,*/}
            {/*        loadingTriggerAuthProcess,*/}
            {/*        role,*/}
            {/*        tokenExpiresIn,*/}
            {/*        permissions: utils.getPermissions()*/}
            {/*    }, null, 2)}*/}
            {/*</pre>*/}
        </AuthContext.Provider>
    );
};

export const useAuthProvider= () => useContext(AuthContext);